import { createStore, useStore } from 'zustand';

import type { SupportDraftInquiry } from '../model/supportContact';

/**
 * The member's unsent message in each support thread: started by the screen
 * that sent them to Primum Support, kept up to date as they type, and dropped
 * once they send.
 *
 * It lives here rather than in the composer because the thread's view can
 * unmount and remount while it loads (the desktop inbox shows a spinner in its
 * place), and a draft held only by the composer, or cleared when the view
 * first read it, would come back empty. It is memory only, so a reload starts
 * clean, and it is never a route param, since screen params reach analytics.
 *
 * `version` counts drafts set by a prompt, not keystrokes, so a view can
 * reseed its composer when a new draft arrives without re-rendering on typing.
 */
type SupportDraft = {
  text: string;
  inquiry?: SupportDraftInquiry;
  version: number;
};

type SupportDraftState = {
  drafts: Record<string, SupportDraft>;
};

export const supportDraftStore = createStore<SupportDraftState>(() => ({ drafts: {} }));

/** Starts a draft for this thread, replacing any unsent text and attachment. */
export function setSupportDraft({
  conversationId,
  text,
  inquiry,
}: {
  conversationId: string;
  text: string;
  inquiry?: SupportDraftInquiry;
}): void {
  supportDraftStore.setState(({ drafts }) => ({
    drafts: {
      ...drafts,
      [conversationId]: { text, inquiry, version: (drafts[conversationId]?.version ?? 0) + 1 },
    },
  }));
}

/** Keeps what the member has typed, without counting as a new draft. */
export function updateSupportDraftText(conversationId: string, text: string): void {
  supportDraftStore.setState(({ drafts }) => ({
    drafts: {
      ...drafts,
      [conversationId]: {
        ...drafts[conversationId],
        text,
        version: drafts[conversationId]?.version ?? 0,
      },
    },
  }));
}

/** Keeps the inquiry the member left attached, or drops it when they remove it. */
export function updateSupportDraftInquiry(
  conversationId: string,
  inquiry: SupportDraftInquiry | undefined
): void {
  supportDraftStore.setState(({ drafts }) => ({
    drafts: {
      ...drafts,
      [conversationId]: {
        text: drafts[conversationId]?.text ?? '',
        inquiry,
        version: drafts[conversationId]?.version ?? 0,
      },
    },
  }));
}

export function useSupportDraftInquiry(conversationId: string): SupportDraftInquiry | undefined {
  return useStore(supportDraftStore, (state) => state.drafts[conversationId]?.inquiry);
}

/** Drops the thread's draft, once the member has sent it. */
export function clearSupportDraft(conversationId: string): void {
  supportDraftStore.setState(({ drafts }) => {
    const { [conversationId]: _sent, ...rest } = drafts;

    return { drafts: rest };
  });
}

export function readSupportDraft(conversationId: string): string | undefined {
  return supportDraftStore.getState().drafts[conversationId]?.text;
}

/** Changes only when a prompt sets a new draft for this thread. */
export function useSupportDraftVersion(conversationId: string): number {
  return useStore(supportDraftStore, (state) => state.drafts[conversationId]?.version ?? 0);
}
